// What a file really is, from its first bytes: the same rules as the API's
// FileSniffer, so the demo and the screens refuse what the server would.

export const ATTACHMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/webp', 'image/tiff'] as const;
export type AttachmentType = (typeof ATTACHMENT_TYPES)[number];
export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;
/** For file pickers: a hint to the browser only; the bytes decide. */
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.webp,.tif,.tiff,application/pdf,image/*';
export const LOGO_ACCEPT = '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp';

const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.slice(from, to));
const u16be = (b: Uint8Array, i: number) => (b[i]! << 8) | b[i + 1]!;
const u16le = (b: Uint8Array, i: number) => b[i]! | (b[i + 1]! << 8);
const u32be = (b: Uint8Array, i: number) => ((b[i]! << 24) >>> 0) + (b[i + 1]! << 16) + (b[i + 2]! << 8) + b[i + 3]!;
const u32le = (b: Uint8Array, i: number) => (b[i]! | (b[i + 1]! << 8) | (b[i + 2]! << 16)) + ((b[i + 3]! << 24) >>> 0);

export function sniff(b: Uint8Array): AttachmentType | null {
  if (b.length >= 5 && ascii(b, 0, 5) === '%PDF-') return 'application/pdf';
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((x, i) => b[i] === x)) return 'image/png';
  if (b.length >= 12 && ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 12) === 'WEBP') return 'image/webp';
  if (b.length >= 12 && ascii(b, 4, 8) === 'ftyp' && ['heic', 'heix', 'heim', 'heis', 'mif1', 'msf1'].includes(ascii(b, 8, 12))) return 'image/heic';
  if (b.length >= 4 && ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2a && b[3] === 0) || (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0 && b[3] === 0x2a))) return 'image/tiff';
  return null;
}

/** Width and height of a PNG, JPEG or WebP, or null. */
export function imageSize(b: Uint8Array): { width: number; height: number } | null {
  const type = sniff(b);
  if (type === 'image/png' && b.length >= 24) return { width: u32be(b, 16), height: u32be(b, 20) };
  if (type === 'image/jpeg') {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i++; continue; }
      const m = b[i + 1]!;
      if (m === 0xff) { i++; continue; }
      if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
      const len = u16be(b, i + 2);
      if (len < 2) return null;
      if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) return { width: u16be(b, i + 7), height: u16be(b, i + 5) };
      i += 2 + len;
    }
    return null;
  }
  if (type === 'image/webp' && b.length >= 30) {
    const chunk = ascii(b, 12, 16);
    if (chunk === 'VP8 ') return { width: u16le(b, 26) & 0x3fff, height: u16le(b, 28) & 0x3fff };
    if (chunk === 'VP8L') { const v = u32le(b, 21); return { width: (v & 0x3fff) + 1, height: ((v >>> 14) & 0x3fff) + 1 }; }
    if (chunk === 'VP8X') return { width: (b[24]! | (b[25]! << 8) | (b[26]! << 16)) + 1, height: (b[27]! | (b[28]! << 8) | (b[29]! << 16)) + 1 };
  }
  return null;
}

/** The first bytes of a file (enough for sniff and imageSize). */
export function readHead(file: Blob, bytes = 64 * 1024): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => reject(r.error ?? new Error('The file couldn’t be read.'));
    r.readAsArrayBuffer(file.slice(0, bytes));
  });
}

export function kindLabel(contentType: string): string {
  return contentType === 'application/pdf' ? 'PDF' : contentType.replace('image/', '').replace('jpeg', 'jpg').toUpperCase();
}
